import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import {
  createRpcClient,
  createBalanceReader,
  type Balance,
} from '@gonka-wallet/chain';
import { useSettingsStore } from '../stores/settingsStore.js';
import { resolveSeeds } from '../lib/resolveSeeds.js';

export interface AggregateBalanceEntry {
  readonly balance: Balance | undefined;
  readonly isLoading: boolean;
}

export interface AggregateBalance {
  /** Sum of total across all addresses (ngonka, string). */
  readonly total: string;
  /** Sum of spendable across all addresses. */
  readonly spendable: string;
  /** Sum of vesting across all addresses (= total - spendable). */
  readonly vesting: string;
  /** True while ANY per-wallet query is in flight. */
  readonly isLoading: boolean;
  /** First error encountered, if any. */
  readonly error: unknown;
  /** Per-address entry with its own loading flag so WalletRow can render a
   *  skeleton for rows that have not yet resolved. Keys are addresses. */
  readonly perAddress: Map<string, AggregateBalanceEntry>;
}

export function useAggregateBalance(
  addresses: readonly string[],
): AggregateBalance {
  const source = useSettingsStore((s) => s.chainNodeSource);
  const customUrl = useSettingsStore((s) => s.customNodeUrl);
  const presetNodeId = useSettingsStore((s) => s.presetNodeId);

  const queries = useQueries({
    queries: addresses.map((addr) => ({
      queryKey: ['balance', addr, source, customUrl, presetNodeId] as const,
      queryFn: async (): Promise<Balance | null> => {
        if (!addr) return null;
        const seeds = resolveSeeds(source, customUrl, presetNodeId);
        const client = createRpcClient(seeds);
        const reader = createBalanceReader(client, null);
        return reader.get(addr);
      },
      enabled: !!addr,
      staleTime: 15_000,
    })),
  }) as UseQueryResult<Balance | null, Error>[];

  const perAddress = new Map<string, AggregateBalanceEntry>();
  let totalSum = 0n;
  let spendableSum = 0n;
  let vestingSum = 0n;
  let anyLoading = false;
  let firstError: unknown = null;

  for (let i = 0; i < addresses.length; i++) {
    const q = queries[i]!;
    const addr = addresses[i]!;
    const entryLoading = q.isLoading || q.isFetching;
    perAddress.set(addr, {
      balance: q.data ?? undefined,
      isLoading: entryLoading,
    });
    if (entryLoading) anyLoading = true;
    if (q.error && !firstError) firstError = q.error;
    if (q.data) {
      totalSum += BigInt(q.data.total);
      spendableSum += BigInt(q.data.spendable);
      vestingSum += BigInt(q.data.vesting);
    }
  }

  return {
    total: totalSum.toString(),
    spendable: spendableSum.toString(),
    vesting: vestingSum.toString(),
    isLoading: anyLoading,
    error: firstError,
    perAddress,
  };
}
